interface ImagePlaceholderProps {
  className?: string
}

export default function ImagePlaceholder({
  className = "",
}: ImagePlaceholderProps) {
  return (
    <div
      className={`relative w-full h-full bg-gradient-to-br from-brand/20 via-system-blue-600/30 to-basic-blue/40 transition-all duration-300 group-hover:from-brand/30 group-hover:via-system-blue-500/40 group-hover:to-basic-blue/50 overflow-hidden ${className}`}
    >
      {/* Geometric shapes */}
      <div className="absolute inset-0 overflow-hidden">
        {/* Large circle */}
        <div className="absolute -top-system-lg -right-system-lg w-system-2xl h-system-2xl bg-brand/20 rounded-full blur-sm transition-transform duration-300 group-hover:scale-110" />
        {/* Medium circle */}
        <div className="absolute top-1/3 -left-system-sm w-system-xl h-system-xl bg-system-blue-400/30 rounded-full blur-sm transition-transform duration-300 group-hover:scale-105" />
        {/* Small circles */}
        <div className="absolute bottom-system-lg right-system-lg w-system-lg h-system-lg bg-brand/40 rounded-full transition-transform duration-300 group-hover:scale-125" />
        <div className="absolute bottom-system-xl left-system-sm w-system-sm h-system-sm bg-system-blue-300/40 rounded-full transition-transform duration-300 group-hover:scale-110" />
        {/* Triangular shapes */}
        <div className="absolute top-system-lg left-system-lg w-0 h-0 border-l-system-lg border-r-system-lg border-b-system-sm border-l-transparent border-r-transparent border-b-brand/25 transition-transform duration-300 group-hover:rotate-12" />
        <div className="absolute bottom-1/4 right-1/4 w-0 h-0 border-l-system-sm border-r-system-sm border-b-system-lg border-l-transparent border-r-transparent border-b-system-blue-400/30 transition-transform duration-300 group-hover:-rotate-12" />
        {/* Rectangular shapes */}
        <div className="absolute top-1/2 left-1/4 w-system-xs h-system-lg bg-brand/15 rounded-sm transform rotate-45 transition-transform duration-300 group-hover:rotate-90" />
        <div className="absolute bottom-1/3 right-1/3 w-system-sm h-system-2xs bg-system-blue-400/25 rounded-sm transform -rotate-12 transition-transform duration-300 group-hover:rotate-12" />
      </div>

      {/* Confetti particles - contained within bounds */}
      <div className="absolute inset-system-xxs pointer-events-none">
        {/* Colorful confetti particles */}
        <div
          className="absolute top-system-xs left-system-xs w-system-xxs h-system-xxs bg-pink-400/80 rounded-full opacity-0 group-hover:opacity-100 group-hover:animate-bounce transition-all duration-500"
          style={{ animationDelay: "0.1s" }}
        />
        <div
          className="absolute top-system-lg right-system-lg w-system-2xs h-system-2xs bg-yellow-400/80 rounded-full opacity-0 group-hover:opacity-100 group-hover:animate-ping transition-all duration-500"
          style={{ animationDelay: "0.2s" }}
        />
        <div
          className="absolute bottom-system-sm left-system-lg w-system-xxs h-system-xxs bg-green-400/80 rounded-full opacity-0 group-hover:opacity-100 group-hover:animate-pulse transition-all duration-500"
          style={{ animationDelay: "0.3s" }}
        />
        <div
          className="absolute top-system-xl right-system-sm w-system-xxs h-system-xxs bg-purple-400/80 rounded-full opacity-0 group-hover:opacity-100 group-hover:animate-bounce transition-all duration-500"
          style={{ animationDelay: "0.4s" }}
        />
        <div
          className="absolute bottom-system-lg right-system-xl w-system-2xs h-system-2xs bg-orange-400/80 rounded-full opacity-0 group-hover:opacity-100 group-hover:animate-ping transition-all duration-500"
          style={{ animationDelay: "0.5s" }}
        />
        <div
          className="absolute bottom-system-xs left-system-xl w-system-xxs h-system-xxs bg-blue-400/80 rounded-full opacity-0 group-hover:opacity-100 group-hover:animate-pulse transition-all duration-500"
          style={{ animationDelay: "0.6s" }}
        />
        <div
          className="absolute top-system-sm left-system-sm w-system-xxs h-system-xxs bg-brand/80 rounded-full opacity-0 group-hover:opacity-100 group-hover:animate-bounce transition-all duration-500"
          style={{ animationDelay: "0.7s" }}
        />
        <div
          className="absolute bottom-system-xl left-system-xs w-system-2xs h-system-2xs bg-pink-500/80 rounded-full opacity-0 group-hover:opacity-100 group-hover:animate-ping transition-all duration-500"
          style={{ animationDelay: "0.8s" }}
        />

        {/* Small sparkle effects */}
        <div
          className="absolute top-system-sm right-system-sm w-system-3xs h-system-3xs bg-white/90 rounded-full opacity-0 group-hover:opacity-100 group-hover:animate-pulse transition-all duration-300"
          style={{ animationDelay: "0.9s" }}
        />
        <div
          className="absolute bottom-system-sm left-system-sm w-system-3xs h-system-3xs bg-white/90 rounded-full opacity-0 group-hover:opacity-100 group-hover:animate-pulse transition-all duration-300"
          style={{ animationDelay: "1s" }}
        />
        <div
          className="absolute top-system-lg left-system-lg w-system-3xs h-system-3xs bg-white/90 rounded-full opacity-0 group-hover:opacity-100 group-hover:animate-pulse transition-all duration-300"
          style={{ animationDelay: "1.1s" }}
        />
        <div
          className="absolute bottom-system-md right-system-md w-system-3xs h-system-3xs bg-white/90 rounded-full opacity-0 group-hover:opacity-100 group-hover:animate-pulse transition-all duration-300"
          style={{ animationDelay: "1.2s" }}
        />
      </div>

      {/* Center icon */}
      <div className="absolute inset-0 flex items-center justify-center">
        <div className="bg-white/10 backdrop-blur-sm rounded-full p-system-xs transition-all duration-300 group-hover:bg-white/20 group-hover:scale-110">
          <svg
            className="w-system-lg h-system-lg text-white/70"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={1.5}
              d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
            />
          </svg>
        </div>
      </div>

      {/* Subtle overlay */}
      <div className="absolute inset-0 bg-gradient-to-t from-basic-black/20 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
    </div>
  )
}
